import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { Bar, Tooltip as RechartsTooltip, Legend, LabelList, YAxis, XAxis, ReferenceLine } from 'recharts';
import {
  CHART_OPACITY,
  BAR_TOP_RADIUS,
  REVERSE_MONTH_KEYS,
  MonthRangeOption,
  LABEL_FONT_SIZE,
  TICK_FONT_SIZE,
  BAR_MARGIN_TIME_SERIES,
  calendarPrevYm,
  compareMonthKeysAsc,
} from '@/domains/global-dashboard/utils/chart-utils';
import { formatNumber } from '@/shared/utils/format-utils';
import {
  DashboardBarChart,
  DashboardBarGrid,
  DashboardBarPlotHoverCursor,
  DashboardChartViewport,
} from '@/domains/global-dashboard/components/GlobalDashboardBarChart';
import { barTopNumericLabelProps } from '@/domains/global-dashboard/components/dashboardChart';
import { useMemo, useState } from 'react';
import {
  TERMINAL_TYPE_STACK,
  terminalMonthsByYmFromOverview,
  TerminalMonthSnap,
} from '@/domains/global-dashboard/sections/terminals/terminal-utils';
import { InventoryForecastCard } from '@/domains/global-dashboard/sections/terminals/InventoryForecastCard';
import { buildInventoryForecastRows } from '@/domains/global-dashboard/sections/terminals/inventory-forecast-utils';
import { TerminalType } from '@/shared/types/userTerminalTypes';
import { TERMINAL_TYPE_INFO } from '@/shared/utils/color-utils-terminals';


/** Field key used in chart rows (`organizationId__typeDataKey`). */
function terminalEvolutionFieldKey(organizationId: string, typeDataKey: string): string {
  return `${organizationId}__${typeDataKey}`;
}

/** Parse `terminalEvolutionFieldKey`. */
function parseTerminalEvolutionFieldKey(raw: unknown): { organizationId: string; typeDataKey: string } | undefined {
  const s = typeof raw === 'string' ? raw : String(raw);
  const sep = '__';
  const idx = s.indexOf(sep);
  if (idx === -1) return undefined;
  return { organizationId: s.slice(0, idx), typeDataKey: s.slice(idx + sep.length) };
}

/** Get the evolution value from two terminal month snapshots for a given temporal mode and slice. */
function evolutionValueFromTerminalSnaps(
  cur: TerminalMonthSnap | undefined,
  prev: TerminalMonthSnap | undefined,
  temporalMode: 'total' | 'deployed' | 'standby' | 'netNew',
  type: TerminalType,
): number {
  const slotCur = cur?.typeSlots.find((slot) => slot.type === type);
  const slotPrev = prev?.typeSlots.find((slot) => slot.type === type);

  if (temporalMode === 'netNew') {
    if (!prev) return 0;
    return (slotCur?.total ?? 0) - (slotPrev?.total ?? 0);
  }
  return slotCur?.[temporalMode] ?? 0;
}

type TerminalEvolutionChartRow = Record<string, string | number> & { month: string };

/** Build one chart row per month. */
function buildTerminalEvolutionRowForMonth(
  ym: string,
  organizationSeries: { organization: OrganizationOverviewData; months: Map<string, TerminalMonthSnap> }[],
  temporal: 'total' | 'deployed' | 'standby' | 'netNew',
): TerminalEvolutionChartRow {
  const prevYm = calendarPrevYm(ym);
  const point: TerminalEvolutionChartRow = { month: ym };
  for (const { organization, months } of organizationSeries) {
    const cur = months.get(ym);
    const prev = months.get(prevYm);
    for (let i = 0; i < TERMINAL_TYPE_STACK.length; i++) {
      const spec = TERMINAL_TYPE_STACK[i]!;
      point[terminalEvolutionFieldKey(organization.organizationId, spec.dataKey)] = evolutionValueFromTerminalSnaps(
        cur,
        prev,
        temporal,
        spec.type,
      );
    }
  }
  return point;
}

export type TerminalEvolutionChartProps = {
  selectedOrganizations: OrganizationOverviewData[];
  accountChartColorById?: Map<string, string>;
  /**
   * Global-dashboard: follow per-market breakdown (All units / Deployed / On-hand) for totals + net-new.
   * Single-account dialogs can omit or pass `'total'`.
   */
  chartClassName?: string;
};

/** Stacked terminal evolution by account × model (months on X). */
export function TerminalEvolutionChart({
  selectedOrganizations,
  accountChartColorById,
}: TerminalEvolutionChartProps) {
  const [terminalTemporalMetric, setTerminalTemporalMetric] = useState<'total' | 'deployed' | 'standby' | 'netNew'>('total');
  const [monthRange, setMonthRange] = useState<MonthRangeOption>(12);

  const organizationSeries = useMemo(
    () =>
      selectedOrganizations.map((organization) => ({
        organization,
        months: terminalMonthsByYmFromOverview(organization),
      })),
    [selectedOrganizations],
  );

  const chartMonthTimeline = useMemo(() => {
    const keys = new Set<string>();
    for (const { months } of organizationSeries) {
      for (const ym of months.keys()) keys.add(ym);
    }
    return keys.size > 0
      ? [...keys].sort(compareMonthKeysAsc)
      : [...REVERSE_MONTH_KEYS];
  }, [organizationSeries]);

  const monthWindow = useMemo(() => chartMonthTimeline.slice(-monthRange), [monthRange, chartMonthTimeline]);

  const overallTerminalEvolutionData = useMemo(() => {
    return chartMonthTimeline
      .map((ym) => buildTerminalEvolutionRowForMonth(ym, organizationSeries, terminalTemporalMetric))
      .sort((a, b) => compareMonthKeysAsc(String(a.month), String(b.month)));
  }, [chartMonthTimeline, organizationSeries, terminalTemporalMetric]);

  const selectedRangeTerminalEvolutionData = useMemo(() => {
    const want = new Set(monthWindow);
    return overallTerminalEvolutionData.filter((row) => want.has(String(row.month)));
  }, [overallTerminalEvolutionData, monthWindow]);

  /** Net-new MoM can go negative; draw a y=0 baseline only when the series dips below zero. */
  const terminalEvolutionNetNewHasNegative = useMemo(() => {
    if (terminalTemporalMetric !== 'netNew') return false;
    for (const row of selectedRangeTerminalEvolutionData) {
      for (const o of selectedOrganizations) {
        const raw = row[o.organizationId];
        const v = typeof raw === 'number' ? raw : Number(raw);
        if (!Number.isNaN(v) && v < 0) return true;
      }
    }
    return false;
  }, [terminalTemporalMetric, selectedRangeTerminalEvolutionData, selectedOrganizations]);

  const organizationsForecast = useMemo(
    () => buildInventoryForecastRows(selectedOrganizations),
    [selectedOrganizations],
  );

  return (
    <div className="space-y-4 overflow-y-auto">
      <Card>
        <CardHeader className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 justify-end items-center w-full">
            <div className="col-span-1">
              <CardTitle className="text-base">Terminal inventory evolution</CardTitle>
              <CardDescription>
                Terminal inventory evolution for the selected time range.
              </CardDescription>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              <ToggleGroup
                type="single"
                value={monthRange.toString()}
                onValueChange={(v) => v && setMonthRange(Number(v) as MonthRangeOption)}
                size="default"
                variant="outline"
              >
                <ToggleGroupItem value="1">1-Month</ToggleGroupItem>
                <ToggleGroupItem value="3">3-Months</ToggleGroupItem>
                <ToggleGroupItem value="6">6-Months</ToggleGroupItem>
                <ToggleGroupItem value="12">12-Months</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <ToggleGroup
              type="single"
              value={terminalTemporalMetric}
              onValueChange={(v) => v && setTerminalTemporalMetric(v as 'total' | 'deployed' | 'standby' | 'netNew')}
              size="default"
              variant="outline"
            >
              <ToggleGroupItem value="total">All units</ToggleGroupItem>
              <ToggleGroupItem value="deployed">Deployed</ToggleGroupItem>
              <ToggleGroupItem value="standby">On-hand</ToggleGroupItem>
              <ToggleGroupItem value="netNew">Net new / month</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <DashboardChartViewport>
            <DashboardBarChart
              data={selectedRangeTerminalEvolutionData}
              variant="timeSeries"
              margin={
                terminalTemporalMetric === 'netNew'
                  ? {
                    ...BAR_MARGIN_TIME_SERIES,
                    bottom: terminalEvolutionNetNewHasNegative ? 88 : BAR_MARGIN_TIME_SERIES.bottom + 12,
                  }
                  : undefined
              }>
              <DashboardBarGrid />
              {terminalEvolutionNetNewHasNegative ? (
                <ReferenceLine y={0} stroke="hsl(var(--border))" strokeWidth={1} />
              ) : null}
              <XAxis dataKey="month" tick={{ fontSize: TICK_FONT_SIZE }} textAnchor="middle" tickMargin={6} />
              <YAxis
                tick={{ fontSize: TICK_FONT_SIZE }}
                allowDecimals={false}
                domain={
                  terminalTemporalMetric === 'netNew' && terminalEvolutionNetNewHasNegative
                    ? [
                      (min: number) => {
                        const m = Math.min(0, min);
                        if (!Number.isFinite(m) || m >= 0) return Math.min(0, min);
                        const pad = Math.max(Math.abs(m) * 0.08, Math.abs(min) > 1 ? 1 : 0.35);
                        return m - pad;
                      },
                      (max: number) => Math.max(0, max),
                    ]
                    : undefined
                }
                label={{
                  value: terminalTemporalMetric === 'netNew' ? 'Net change (units)' : 'Units',
                  angle: -90,
                  position: 'middle',
                  fontSize: LABEL_FONT_SIZE,
                }}
              />
              <RechartsTooltip
                cursor={<DashboardBarPlotHoverCursor />}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const row = payload[0]?.payload as Record<string, unknown>;
                  const month = String(row.month);

                  type TooltipLine = { legendShort: string; fill: string; value: number };
                  const grouped = new Map<string, { name: string; marketColor: string; lines: TooltipLine[] }>();

                  for (const p of payload) {
                    const parsed = parseTerminalEvolutionFieldKey(p.dataKey);
                    if (!parsed) continue;
                    const o = selectedOrganizations.find((x) => x.organizationId === parsed.organizationId);
                    const spec = TERMINAL_TYPE_STACK.find((t) => t.dataKey === parsed.typeDataKey);
                    if (!o || !spec) continue;
                    const value = Number(p.value ?? 0);
                    const dot = accountChartColorById?.get(o.organizationId) ?? 'hsl(var(--primary))';
                    const entry =
                      grouped.get(o.organizationId) ?? {
                        name: o.name,
                        marketColor: dot,
                        lines: [] as TooltipLine[],
                      };
                    entry.lines.push({ legendShort: TERMINAL_TYPE_INFO[spec.type].key, fill: spec.fill, value });
                    grouped.set(o.organizationId, entry);
                  }

                  const tooltipSecondary =
                    terminalTemporalMetric === 'deployed'
                      ? 'Deployed by type'
                      : terminalTemporalMetric === 'standby'
                        ? 'On-hand by type'
                        : terminalTemporalMetric === 'total'
                          ? 'All inventory by type'
                          : terminalTemporalMetric === 'netNew'
                            ? 'Net new by type'
                            : 'All inventory by type';

                  return (
                    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-md min-w-[220px]">
                      <p className="font-medium mb-1">{month}</p>
                      <p className="mb-2 text-[11px]">
                        {terminalTemporalMetric === 'total'
                          ? 'Monthly totals'
                          : terminalTemporalMetric === 'deployed'
                            ? 'Deployed counts'
                            : terminalTemporalMetric === 'standby'
                              ? 'On-hand counts'
                              : 'Net vs prior month'}
                        {' · '}
                        {tooltipSecondary}
                      </p>
                      <div className="space-y-2">
                        {selectedOrganizations.map((o) => {
                          const g = grouped.get(o.organizationId);
                          if (!g?.lines.length) return null;
                          return (
                            <div key={o.organizationId}>
                              <p className="font-medium mb-0.5 flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-sm shrink-0" style={{ backgroundColor: g.marketColor }} />
                                {g.name}
                              </p>
                              <div className="pl-4 space-y-0.5">
                                {TERMINAL_TYPE_STACK.map((spec) => {
                                  const line = g.lines.find((l) => l.legendShort === TERMINAL_TYPE_INFO[spec.type].key);
                                  if (!line || (line.value === 0 && terminalTemporalMetric !== 'netNew')) return null;
                                  return (
                                    <p key={spec.dataKey} className="tabular-nums flex justify-between gap-4">
                                      <span style={{ color: line.fill }}>{TERMINAL_TYPE_INFO[spec.type].value}</span>
                                      <span>{formatNumber(line.value)}</span>
                                    </p>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }}
              />
              <Legend
                content={() => (
                  <div className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 text-xs pt-2">
                    {TERMINAL_TYPE_STACK.map((s) => (
                      <span key={s.dataKey} className="inline-flex items-center gap-1.5 text-sm">
                        <span
                          className="h-2.5 w-2.5 rounded-sm shrink-0 ring-1 ring-border/60"
                          style={{ background: s.fill }}
                        />
                        {TERMINAL_TYPE_INFO[s.type].value}
                      </span>
                    ))}
                  </div>
                )}
              />
              {selectedOrganizations.flatMap((o) =>
                TERMINAL_TYPE_STACK.map((spec, typeIdx) => {
                  const isTop = typeIdx === TERMINAL_TYPE_STACK.length - 1;
                  const field = terminalEvolutionFieldKey(o.organizationId, spec.dataKey);
                  return (
                    <Bar
                      key={`${o.organizationId}-${spec.dataKey}`}
                      dataKey={field}
                      stackId={o.organizationId}
                      name={`${o.name} · ${TERMINAL_TYPE_INFO[spec.type].value}`}
                      fill={spec.fill}
                      fillOpacity={CHART_OPACITY.terminalTypeStack}
                      legendType="none"
                      radius={isTop ? BAR_TOP_RADIUS : [0, 0, 0, 0]}
                    >
                      {isTop && (
                        <LabelList
                          {...barTopNumericLabelProps}
                          formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : Number(value))}
                        />
                      )}
                    </Bar>
                  );
                }),
              )}
            </DashboardBarChart>
          </DashboardChartViewport>
        </CardContent>
      </Card>

      {organizationsForecast.length > 0 && <InventoryForecastCard organizationsForecast={organizationsForecast} />}
    </div>
  );
}
